// @flow

import React from "react";
import pick from "lodash/pick";
import pickBy from "lodash/pickBy";
import keys from "lodash/keys";
import difference from "lodash/difference";

import Section from "../../components/Section";
import EditableForm from "../../components/EditableForm";
import FormRow from "../../components/FormRow";
import DataFetcher from "../../components/DataFetcher";
import ConditionalSubform from "../../components/ConditionalSubform";
import EditableField from "../../components/EditableField";
import * as formConditions from "../../utils/formConditions";
import * as mappers from "../../utils/mappers";
import * as v from "../../utils/validators";
import { errors } from "../../utils/errors";
import { denormalizeJsonApi } from "../../utils/denormalizeJsonApi";
import { type BankActions } from "./Details";

import BankAsyncSelect from "./BankAsyncSelect";

import {
  ALREADY_AT_THE_BANK,
  USED_BANKS,
  BANKS_TO_FORWARD,
  BANK_SUGGESTIONS,
  EXCLUDED_BANKS
} from "./formFields";

type BankProps = {
  initialValues: {},
  bankActions: BankActions,
  inquiryId: number,
  setIsBankDataLoading: boolean => void,
  setBankData: () => {},
  onError: number => void
};

const Bank = ({
  initialValues,
  bankActions,
  inquiryId,
  setIsBankDataLoading,
  onError,
  setBankData
}: BankProps) => {
  const {
    getBanksForDetails,
    createExcludedBank,
    deleteExcludedBank,
    updateBankSuggestionContactPerson,
    updateUsedBanksContactPerson
  } = bankActions;

  const handleSubmit = (values, form, banksData, requestFetch) => {
    setIsBankDataLoading(true);

    const changedFields = pick(values, keys(pickBy(form.getState().dirtyFields)));

    const requests = [];

    if (changedFields[EXCLUDED_BANKS]) {
      const initialBankIds = banksData.excludedBanks.map(({ bank: { id } }) => id);

      const addedBanks = difference(changedFields[EXCLUDED_BANKS], initialBankIds);
      const removedBanks = difference(initialBankIds, changedFields[EXCLUDED_BANKS]);

      if (addedBanks.length) {
        addedBanks.forEach(bankId =>
          requests.push(createExcludedBank({ id: inquiryId, bankId: Number(bankId) }))
        );
      }

      if (removedBanks.length) {
        const excludedBankIds = removedBanks.map(bankId =>
          banksData.excludedBanks.find(({ bank: { id } }) => id === bankId)
        );
        excludedBankIds.forEach(({ id: excludedBankId }) =>
          requests.push(deleteExcludedBank({ id: inquiryId, bankId: Number(excludedBankId) }))
        );
      }
    }

    if (changedFields[USED_BANKS]) {
      values[USED_BANKS].filter(({ contact }) => !!contact).forEach(usedBank =>
        requests.push(
          updateUsedBanksContactPerson({
            id: inquiryId,
            first_name: usedBank.contact && usedBank.contact.firstName,
            last_name: usedBank.contact && usedBank.contact.lastName,
            bankId: Number(usedBank.id)
          })
        )
      );
    }

    if (changedFields[BANK_SUGGESTIONS]) {
      values[BANK_SUGGESTIONS].filter(({ contact }) => !!contact).forEach(bankSuggestion =>
        requests.push(
          updateBankSuggestionContactPerson({
            id: inquiryId,
            first_name: bankSuggestion.contact.firstName,
            last_name: bankSuggestion.contact.lastName,
            bankId: Number(bankSuggestion.id)
          })
        )
      );
    }

    if (requests.length) {
      Promise.all(requests)
        .then(() => {
          requestFetch();
        })
        .catch(err => {
          onError(err.response && err.response.status);
        });
    }
  };

  const { fieldHasValue } = formConditions;
  const { maxLength, optionalCombineValidators, isFilledAndIsRelatedFieldFilled } = v;
  const hasBanksToForward = formConditions.fieldHasValue(BANKS_TO_FORWARD, true);

  return (
    <DataFetcher
      apiAction={getBanksForDetails}
      mapper={([usedBanksResponse, excludedBanksResponse, inquiryBankSuggestionsResponse]) => ({
        usedBanks: denormalizeJsonApi(usedBanksResponse),
        excludedBanks: denormalizeJsonApi(excludedBanksResponse),
        bankSuggestions: denormalizeJsonApi(inquiryBankSuggestionsResponse)
      })}
      setData={setBankData}
      initialPayload={{ id: inquiryId }}
    >
      {({ data, isLoading, requestFetch }) => {
        setIsBankDataLoading(isLoading);

        const banksData = !isLoading
          ? data
          : {
              usedBanks: [],
              excludedBanks: [],
              bankSuggestions: []
            };

        return (
          <Section title="Bankinformationen" reducedMargin numbered>
            <EditableForm
              initialValues={{
                [ALREADY_AT_THE_BANK]: initialValues[ALREADY_AT_THE_BANK],
                [USED_BANKS]: banksData.usedBanks,
                [EXCLUDED_BANKS]: banksData.excludedBanks.map(({ bank: { id } }) => id),
                [BANKS_TO_FORWARD]: initialValues[BANKS_TO_FORWARD],
                [BANK_SUGGESTIONS]: banksData.bankSuggestions
              }}
              isLoading={isLoading}
              onSubmit={(values, form) => handleSubmit(values, form, banksData, requestFetch)}
            >
              {!isLoading && (
                <>
                  <FormRow separator>
                    <EditableField
                      fieldControlProps={{
                        name: ALREADY_AT_THE_BANK,
                        caption: () => "Liegt diese Anfrage schon einer Bank vor?"
                      }}
                      displayValue={mappers.yesNoMapper}
                    />
                    <div />
                    <div />
                  </FormRow>
                  <ConditionalSubform
                    condition={fieldHasValue(ALREADY_AT_THE_BANK, true)}
                    renderOnSatisfy={() => (
                      <>
                        {banksData.usedBanks.length &&
                          banksData.usedBanks.map((bank, index) => (
                            <FormRow separator key={bank.id}>
                              <EditableField
                                fieldControlProps={{
                                  name: `used_banks[${index}].bank.name`,
                                  caption: () => "Bank"
                                }}
                              />
                              <EditableField
                                fieldControlProps={{
                                  name: `used_banks[${index}].contact.firstName`,
                                  caption: () => "Kundenbetreuer Vorname",
                                  validate: optionalCombineValidators(
                                    isFilledAndIsRelatedFieldFilled(
                                      `used_banks[${index}].contact.lastName`,
                                      errors.withLastName()
                                    ),
                                    maxLength(100, errors.maxLengthCharacters(100))
                                  )
                                }}
                                type="textInput"
                                inputProps={{ placeholder: "Vorname" }}
                              />
                              <EditableField
                                fieldControlProps={{
                                  name: `used_banks[${index}].contact.lastName`,
                                  caption: () => "Kundenbetreuer Nachname",
                                  validate: optionalCombineValidators(
                                    isFilledAndIsRelatedFieldFilled(
                                      `used_banks[${index}].contact.firstName`,
                                      errors.withFirstName()
                                    ),
                                    maxLength(100, errors.maxLengthCharacters(100))
                                  )
                                }}
                                type="textInput"
                                inputProps={{ placeholder: "Nachname" }}
                              />
                            </FormRow>
                          ))}
                      </>
                    )}
                    renderOnFail={() => null}
                  />
                  <FormRow separator={hasBanksToForward}>
                    <EditableField
                      fieldControlProps={{
                        name: BANKS_TO_FORWARD,
                        caption: () =>
                          "Sollen wir die Anfrage Ihrer Hausbank bzw. Wunschbank(en) zusenden?"
                      }}
                      displayValue={mappers.yesNoMapper}
                    />
                    <div />
                    <div />
                  </FormRow>
                  <ConditionalSubform
                    condition={hasBanksToForward}
                    renderOnSatisfy={() => (
                      <>
                        {banksData.bankSuggestions.length &&
                          banksData.bankSuggestions.map((bank, index) => (
                            <FormRow separator key={bank.id}>
                              <EditableField
                                fieldControlProps={{
                                  name: `bank_suggestions[${index}].bank.name`,
                                  caption: () => "Hausbank"
                                }}
                              />
                              <EditableField
                                fieldControlProps={{
                                  name: `bank_suggestions[${index}].contact.firstName`,
                                  caption: () => "Kundenbetreuer Vorname",
                                  validate: optionalCombineValidators(
                                    isFilledAndIsRelatedFieldFilled(
                                      `bank_suggestions[${index}].contact.lastName`,
                                      errors.withLastName()
                                    ),
                                    maxLength(100, errors.maxLengthCharacters(100))
                                  )
                                }}
                                type="textInput"
                                inputProps={{ placeholder: "Vorname" }}
                              />
                              <EditableField
                                fieldControlProps={{
                                  name: `bank_suggestions[${index}].contact.lastName`,
                                  caption: () => "Kundenbetreuer Nachname",
                                  validate: optionalCombineValidators(
                                    isFilledAndIsRelatedFieldFilled(
                                      `bank_suggestions[${index}].contact.firstName`,
                                      errors.withFirstName()
                                    ),
                                    maxLength(100, errors.maxLengthCharacters(100))
                                  )
                                }}
                                type="textInput"
                                inputProps={{ placeholder: "Nachname" }}
                              />
                            </FormRow>
                          ))}
                      </>
                    )}
                    renderOnFail={() => null}
                  />
                  <FormRow separator>
                    <BankAsyncSelect
                      banksData={banksData}
                      bankActions={bankActions}
                      onError={onError}
                    />
                  </FormRow>
                </>
              )}
            </EditableForm>
          </Section>
        );
      }}
    </DataFetcher>
  );
};

export default Bank;
